// Drawing a league view that might not have arrived. BACKLOG E12.
//
// Every league screen used to be handed a value, because the simulator is
// synchronous and always has one. A real league has three answers — not yet,
// here it is, and no — and E12's done-criteria names all three: *"every
// loading, empty and failed state is a `Snapshot` state and not a spinner that
// never resolves."*
//
// FOUR STATES, AND THE FOURTH IS THE ONE WORTH HAVING.
//
//   loading            — nothing to draw yet. A quiet line, not a spinner:
//                        a spinner promises motion and this is usually 40ms.
//   failed, no value   — the server's own sentence, which was written for the
//                        person ("That league is full — ask Dave to open a
//                        seat"), plus the code for anyone reading over a
//                        shoulder, and Try again. Never "Something went
//                        wrong". A server that never answers lands here too,
//                        after `NO_ANSWER_MS` (B-133), rather than loading
//                        forever.
//   ready              — draw it.
//   failed, with value — DRAW THE VALUE, and say it is old. A refresh that
//                        failed must not blank a screen somebody is reading.
//                        This is why `Snapshot` carries `lastValue` at all,
//                        and skipping it is how a poll turns a working screen
//                        into an error page once a minute.
//
// `stale` is deliberately not styled as an error. It is a fact about the
// number's age, and the screens that care can read `asOf` themselves.
//
// BUT A FAILED REFRESH IS SAID, every time, above the value (`StaleNotice`,
// B-222). Until 2026-09-30 the failed-with-value state drew the value and
// nothing else, so a screen whose server had stopped answering looked exactly
// like a screen that was up to date; only the pre-draft page said so. A
// refresh that is merely in flight (`ready` with `stale: true`) says nothing:
// that is ordinary and usually 40ms.
//
// AND SAID ONCE WHILE THE CONNECTION IS DOWN (B-558). The connection line
// across the app already says nothing will update and why, so each screen
// says only how old its numbers are, *As of Sun 1:42 PM*, as a quiet line
// rather than a second warning with a Try again that cannot work. The value
// may be from this device rather than this page (B-557); either way the time
// is the one the answer carried.

import type { ReactNode } from 'react';
import { whenInZone, type Snapshot } from '@fsp/domain';
import { Cap, EmptyState } from '../../kit';
import { Icon } from '../../kit/Icon';
import { Button, Callout, Line, Stack } from '../../kit/ui';
import { useConnection } from '../../data/connection';
import { useReaderZone } from '../../data/hooks';

/**
 * "Could not refresh — this is from Wed 2:14 PM MDT", while a read has failed
 * over a value it still shows; null otherwise, and for a refresh merely in
 * flight. The same sentence at every detail level: it is a warning about what
 * is on screen, not a number.
 */
export function staleSentence<T>(
  snapshot: Snapshot<T>,
  zone: string | null,
  now: number,
): string | null {
  if (snapshot.state !== 'failed' || snapshot.lastValue === null) return null;
  return snapshot.lastAsOf !== null && zone !== null
    ? `Could not refresh — this is from ${whenInZone(snapshot.lastAsOf, zone, now)}.`
    : 'Could not refresh — this may be out of date.';
}

/**
 * "As of Wed 2:14 PM MDT", for a read that has failed over a value it still
 * shows while the connection line is up; null otherwise.
 */
export function asOfSentence<T>(
  snapshot: Snapshot<T>,
  zone: string | null,
  now: number,
): string | null {
  if (snapshot.state !== 'failed' || snapshot.lastValue === null) return null;
  return snapshot.lastAsOf !== null && zone !== null
    ? `As of ${whenInZone(snapshot.lastAsOf, zone, now)}.`
    : 'From earlier.';
}

/** `staleSentence` above the screen, with Try again; `asOfSentence` while the connection is down. */
export function StaleNotice<T>({
  snapshot,
  onRetry,
}: {
  snapshot: Snapshot<T>;
  onRetry?: () => void;
}) {
  const zone = useReaderZone();
  const connection = useConnection();
  const down =
    connection === 'offline' || connection === 'unreachable' || connection === 'reconnecting';
  if (down) {
    const asOf = asOfSentence(snapshot, zone, Date.now());
    if (asOf === null) return null;
    return (
      <div className="fsp-stale-notice fsp-stale-asof" role="note">
        <Icon name="pending" size="sm" />
        <span>{asOf}</span>
      </div>
    );
  }
  const sentence = staleSentence(snapshot, zone, Date.now());
  if (sentence === null) return null;
  return (
    <div className="fsp-stale-notice">
      <Callout tone="warn">
        <Line spread gap={2}>
          <span className="fsp-ui-wrap-text">{sentence}</span>
          <Button size="xs" onClick={onRetry ?? (() => window.location.reload())}>
            Try again
          </Button>
        </Line>
      </Callout>
    </div>
  );
}

export function FromLeague<T>({
  snapshot,
  children,
  /** What to say while there is nothing. Screens with a shape to hold can pass their own. */
  whileLoading,
  onRetry,
}: {
  snapshot: Snapshot<T>;
  children: (value: T, meta: { stale: boolean; asOf: string | null }) => ReactNode;
  whileLoading?: ReactNode;
  /**
   * Ask again. A screen holding its read's `reload` passes it; without one,
   * Try again reloads the page, which asks every read on it again — the
   * right thing after a server that stopped answering.
   */
  onRetry?: () => void;
}) {
  if (snapshot.state === 'loading') {
    return <>{whileLoading ?? <EmptyState>Loading…</EmptyState>}</>;
  }

  if (snapshot.state === 'failed' && snapshot.lastValue === null) {
    // Fail loudly, in the server's words. A blank region here reads as "this
    // league has no standings", which is a different and untrue statement.
    return (
      <EmptyState>
        <Stack gap={2} className="fsp-from-league-failed">
          <div>{snapshot.failure.message}</div>
          <Cap>{snapshot.failure.code}</Cap>
          <Button size="xs" onClick={onRetry ?? (() => window.location.reload())}>
            Try again
          </Button>
        </Stack>
      </EmptyState>
    );
  }

  const value = snapshot.state === 'ready' ? snapshot.value : snapshot.lastValue!;
  const stale = snapshot.state === 'failed' || snapshot.stale;
  const asOf = snapshot.state === 'ready' ? snapshot.asOf : snapshot.lastAsOf;
  return (
    <>
      <StaleNotice snapshot={snapshot} onRetry={onRetry} />
      {children(value, { stale, asOf })}
    </>
  );
}
